import * as React from 'react';
import { cn } from '@/client/lib/utils';

/**
 * Event status badge with consistent color coding across the application
 * Colors:
 * - Blue: new, available
 * - Red: live, disconnected
 * - Orange: on break, starting, stopping
 * - Gray: publishing, unpublishing
 * - Green: published
 * - Gray outline: upcoming (default)
 */

interface EventStatusBadgeProps {
  status: string;
  className?: string;
}

function getStatusColors(status: string): { bgColor: string; textColor: string } {
  const normalizedStatus = status.toLowerCase();

  switch (normalizedStatus) {
    case 'new':
    case 'available':
      return { bgColor: 'bg-blue-600', textColor: 'text-white' };
    case 'live':
    case 'disconnected':
      return { bgColor: 'bg-red-600', textColor: 'text-white' };
    case 'on break':
    case 'starting':
    case 'stopping':
      return { bgColor: 'bg-orange-500', textColor: 'text-white' };
    case 'editing':
    case 'publishing':
    case 'unpublishing':
      return { bgColor: 'bg-gray-500', textColor: 'text-white' };
    case 'published':
      return { bgColor: 'bg-green-600', textColor: 'text-white' };
    case 'upcoming':
    default:
      return { bgColor: 'bg-gray-100', textColor: 'text-gray-800' };
  }
}

export function EventStatusBadge({ status, className }: EventStatusBadgeProps) {
  const { bgColor, textColor } = getStatusColors(status);

  return (
    <span
      className={cn(
        'inline-flex items-center px-2 py-0.5 rounded-full text-xs font-mono font-medium',
        bgColor,
        textColor,
        className
      )}
    >
      {status}
    </span>
  );
}
